/**
 * Parse a UTC datetime string from the database.
 * Database stores times in UTC (YYYY-MM-DD HH:MM:SS format).
 * Handles both "YYYY-MM-DD HH:MM:SS" and ISO formats.
 * Converts to proper ISO 8601 format for consistent browser parsing.
 */
export function parseLocalDateTime(dateStr: string): Date {
  // If it already has proper ISO format with T and Z, parse as-is
  if (dateStr.includes('T') && dateStr.endsWith('Z')) {
    return new Date(dateStr)
  }
  // If it has T but no Z, add Z for UTC
  if (dateStr.includes('T')) {
    return new Date(dateStr + 'Z')
  }
  // Convert DB format "YYYY-MM-DD HH:MM:SS" to ISO format "YYYY-MM-DDTHH:MM:SSZ"
  // Replace the space with 'T' and append 'Z' for UTC
  return new Date(dateStr.replace(' ', 'T') + 'Z')
}

/**
 * Format a UTC date string to user's local time for display.
 * Uses browser's timezone automatically.
 */
export function formatLocalTime(utcDateStr: string, options?: Intl.DateTimeFormatOptions): string {
  const date = parseLocalDateTime(utcDateStr)
  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  }
  return date.toLocaleTimeString('es', options || defaultOptions)
}

/**
 * Convert a local time input to UTC ISO string for API submission.
 */
export function localToUTC(localDateStr: string): string {
  const date = new Date(localDateStr)
  return date.toISOString()
}

/**
 * Get user's timezone offset in hours from UTC.
 * Positive values are ahead of UTC (e.g., +2 for CEST).
 * Negative values are behind UTC (e.g., -5 for Colombia).
 */
export function getUserTimezoneOffset(): number {
  return -new Date().getTimezoneOffset() / 60
}

/**
 * Formats a date as relative time in Spanish
 * Examples: "ahora", "en 5 min", "en 2 horas", "hace 30 min"
 */
export function formatRelativeTime(date: Date | string): string {
  const targetDate = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = targetDate.getTime() - now.getTime()
  const diffMinutes = Math.round(diffMs / (1000 * 60))
  const diffHours = Math.round(diffMs / (1000 * 60 * 60))

  // Past times (overdue)
  if (diffMinutes < -60) {
    const hoursAgo = Math.abs(diffHours)
    return `hace ${hoursAgo} ${hoursAgo === 1 ? 'hora' : 'horas'}`
  }
  if (diffMinutes < -1) {
    return `hace ${Math.abs(diffMinutes)} min`
  }
  if (diffMinutes < 1) {
    return 'ahora'
  }

  // Future times
  if (diffMinutes < 60) {
    return `en ${diffMinutes} min`
  }
  if (diffHours < 24) {
    return `en ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`
  }

  // Tomorrow or later - show date
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(0, 0, 0, 0)

  const dayAfterTomorrow = new Date(tomorrow)
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1)

  if (targetDate >= tomorrow && targetDate < dayAfterTomorrow) {
    return `mañana ${targetDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  }

  return targetDate.toLocaleDateString()
}

/**
 * Returns urgency level based on time until dose
 * - 'overdue': past scheduled time
 * - 'urgent': < 30 minutes
 * - 'soon': < 2 hours
 * - 'normal': >= 2 hours
 */
export function getDoseUrgency(scheduledFor: Date | string): 'overdue' | 'urgent' | 'soon' | 'normal' {
  const targetDate = typeof scheduledFor === 'string' ? parseLocalDateTime(scheduledFor) : scheduledFor
  const now = new Date()
  const diffMs = targetDate.getTime() - now.getTime()
  const diffMinutes = diffMs / (1000 * 60)

  if (diffMinutes < 0) return 'overdue'
  if (diffMinutes < 30) return 'urgent'
  if (diffMinutes < 120) return 'soon'
  return 'normal'
}

/**
 * Formats a date as distance from now in Spanish (for past dates)
 * Examples: "hace unos segundos", "hace 5 min", "hace 2 horas", "hace 3 días"
 */
export function formatDistanceToNow(date: Date | string): string {
  const targetDate = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - targetDate.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)

  if (diffSeconds < 60) {
    return 'hace unos segundos'
  }
  if (diffMinutes < 60) {
    return `hace ${diffMinutes} min`
  }
  if (diffHours < 24) {
    return `hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`
  }
  if (diffDays < 7) {
    return `hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`
  }
  if (diffWeeks < 4) {
    return `hace ${diffWeeks} ${diffWeeks === 1 ? 'semana' : 'semanas'}`
  }
  if (diffMonths < 12) {
    return `hace ${diffMonths} ${diffMonths === 1 ? 'mes' : 'meses'}`
  }

  // Show full date for older entries
  return targetDate.toLocaleDateString('es', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Formats a time string (HH:MM) to locale time format
 * Example: "14:30" -> "2:30 p.m." or "14:30" depending on locale
 */
export function formatTime(time: string | null | undefined): string {
  if (!time) return ''
  const parts = time.split(':')
  const hours = Number(parts[0])
  const minutes = Number(parts[1])
  if (isNaN(hours) || isNaN(minutes)) return ''
  const date = new Date()
  date.setHours(hours, minutes, 0, 0)
  return date.toLocaleTimeString('es', { hour: 'numeric', minute: '2-digit' })
}

/**
 * Formats a date string with optional time
 * Example: "2026-04-29", "14:30" -> "29 abr 2026, 2:30 p.m."
 */
export function formatDateTime(
  dateStr: string,
  timeStr?: string | null
): string {
  const date = new Date(dateStr)
  const formattedDate = date.toLocaleDateString('es', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  if (timeStr) {
    return `${formattedDate}, ${formatTime(timeStr)}`
  }
  return formattedDate
}
